import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import { ApiError, ValidationError } from "@/api/errors";

/**
 * Put a crossbar 400 onto the form fields it names, and toast anything
 * that is not a field error. Returns true when the error was handled.
 */
export function applyServerErrors<T extends FieldValues>(form: UseFormReturn<T>, err: unknown): boolean {
  if (err instanceof ValidationError) {
    const fields = err.fieldErrors();
    const known = flattenPaths(form.getValues());
    const unplaced: { path: string; message: string }[] = [];
    for (const f of fields) {
      if (known.has(f.path)) form.setError(f.path as Path<T>, { type: "server", message: f.message });
      else unplaced.push(f);
    }
    if (unplaced.length > 0) {
      const message = unplaced.map((f) => `${f.path}: ${f.message}`).join("; ");
      form.setError("root.server", { type: "server", message });
      // The form may not render root errors; make sure the operator sees it.
      toast.error(message);
    }
    return true;
  }
  if (err instanceof ApiError) {
    toast.error(describeApiError(err));
    return true;
  }
  return false;
}

/** Dotted paths of every leaf (and every object node) in a values object. */
function flattenPaths(values: unknown, prefix = "", out = new Set<string>()): Set<string> {
  if (!values || typeof values !== "object" || Array.isArray(values)) return out;
  for (const [k, v] of Object.entries(values as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k;
    out.add(path);
    if (v && typeof v === "object" && !Array.isArray(v)) flattenPaths(v, path, out);
  }
  return out;
}

export function describeApiError(err: unknown): string {
  if (err instanceof ApiError) {
    const id = err.requestId ? ` (request ${err.requestId})` : "";
    switch (err.status) {
      case 403:
        return `Not allowed${id}`;
      case 404:
        return `Not found${id}`;
      case 409:
        return `Someone else saved this first. Reload and try again${id}`;
      case 429:
        return `The API is rate limiting us. Wait a moment${id}`;
      default:
        return `${err.message}${id}`;
    }
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Run a mutation with a success toast and a described failure toast.
 * Returns true when it succeeded; a declined `withCharges` quote (its
 * "cancelled" rejection) is neither success nor an error. `describe`
 * lets a screen say what a failure means there (a webhook's URL the
 * cluster cannot resolve, a service that is not running) instead of the
 * generic wording.
 */
export async function attempt(label: string, run: () => Promise<unknown>, done?: () => void, describe: (err: unknown) => string = describeApiError): Promise<boolean> {
  try {
    await run();
    if (label) toast.success(label);
    done?.();
    return true;
  } catch (err) {
    if (err instanceof Error && err.message === "cancelled") return false;
    toast.error(describe(err));
    return false;
  }
}
